import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Divider,
} from '@mui/material';

const skeletonSx = {
  '&::after': {
    animationDuration: '1.6s',
    animationDelay: '0s'
  },
  '&::before': {
    animationDelay: '0s'
  }
};

/**
 * Skeleton for a single match card in the schedule
 */
export const MatchCardSkeleton: React.FC = () => {
  return (
    <Grid item xs={12}>
      <Card sx={{ borderLeft: '4px solid #e0e0e0' }}>
        <CardContent>
          {/* Top Section: Time Badge and Location */}
          <Box mb={2}>
            <Box
              display={{ xs: 'none', sm: 'flex' }}
              alignItems="center"
              justifyContent="space-between"
            >
              {/* Time badge skeleton */}
              <Skeleton
                variant="rectangular"
                width={80}
                height={28}
                animation="wave"
                sx={{ borderRadius: 2, ...skeletonSx }}
              />
              {/* Location skeleton */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Skeleton variant="text" width={150} height={20} animation="wave" sx={skeletonSx} />
                <Skeleton variant="text" width={100} height={16} animation="wave" sx={skeletonSx} />
              </Box>
            </Box>

            {/* Mobile Layout */}
            <Box display={{ xs: 'block', sm: 'none' }} textAlign="center">
              <Skeleton
                variant="rectangular"
                width={80}
                height={28}
                animation="wave"
                sx={{ borderRadius: 2, mx: 'auto', mb: 1, ...skeletonSx }}
              />
              <Skeleton variant="text" width="60%" height={20} animation="wave" sx={{ mx: 'auto', ...skeletonSx }} />
            </Box>
          </Box>

          {/* Teams Section */}
          <Grid container spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            {/* Home Team */}
            <Grid item xs={4.5} sm={5}>
              <Box display="flex" alignItems="center" justifyContent="flex-end" flexDirection={{ xs: 'column', sm: 'row' }}>
                <Box textAlign={{ xs: 'center', sm: 'right' }} order={{ xs: 2, sm: 1 }} mr={{ xs: 0, sm: 2 }}>
                  <Skeleton variant="text" width={100} height={24} animation="wave" sx={skeletonSx} />
                  <Skeleton variant="text" width={70} height={16} animation="wave" sx={skeletonSx} />
                </Box>
                <Box order={{ xs: 1, sm: 2 }} mb={{ xs: 0.5, sm: 0 }}>
                  <Skeleton variant="circular" width={32} height={32} animation="wave" sx={skeletonSx} />
                </Box>
              </Box>
            </Grid>

            {/* VS */}
            <Grid item xs={3} sm={2}>
              <Box textAlign="center" display="flex" alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={30} height={24} animation="wave" sx={skeletonSx} />
              </Box>
            </Grid>

            {/* Away Team */}
            <Grid item xs={4.5} sm={5}>
              <Box display="flex" alignItems="center" flexDirection={{ xs: 'column', sm: 'row' }}>
                <Box order={{ xs: 1, sm: 1 }} mb={{ xs: 0.5, sm: 0 }}>
                  <Skeleton variant="circular" width={32} height={32} animation="wave" sx={skeletonSx} />
                </Box>
                <Box textAlign={{ xs: 'center', sm: 'left' }} order={{ xs: 2, sm: 2 }} ml={{ xs: 0, sm: 2 }}>
                  <Skeleton variant="text" width={100} height={24} animation="wave" sx={skeletonSx} />
                  <Skeleton variant="text" width={70} height={16} animation="wave" sx={skeletonSx} />
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Bottom Section: Player info and departure time */}
          <Box
            display="flex"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 1, sm: 0 }}
          >
            {/* Player Avatars and info */}
            <Box display="flex" alignItems="center">
              <Box display="flex" sx={{ mr: 1 }}>
                <Skeleton variant="circular" width={24} height={24} animation="wave" sx={skeletonSx} />
                <Skeleton variant="circular" width={24} height={24} animation="wave" sx={{ ml: -0.5, ...skeletonSx }} />
              </Box>
              <Box>
                <Skeleton variant="text" width={180} height={20} animation="wave" sx={skeletonSx} />
                <Skeleton variant="text" width={100} height={16} animation="wave" sx={skeletonSx} />
              </Box>
            </Box>

            {/* Departure time skeleton */}
            <Box display="flex" alignItems="center" gap={1}>
              <Skeleton variant="circular" width={24} height={24} animation="wave" sx={skeletonSx} />
              <Skeleton variant="text" width={120} height={20} animation="wave" sx={skeletonSx} />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

/**
 * Skeleton for a day's worth of matches
 */
export const DayMatchesSkeleton: React.FC<{ matchCount?: number }> = ({ matchCount = 2 }) => {
  return (
    <Box mb={3}>
      {/* Date header skeleton */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Skeleton variant="text" width={180} height={32} animation="wave" sx={skeletonSx} />
      </Box>

      {/* Match cards skeleton */}
      <Grid container spacing={2}>
        {Array.from({ length: matchCount }).map((_, index) => (
          <MatchCardSkeleton key={index} />
        ))}
      </Grid>
    </Box>
  );
};

/**
 * Skeleton for the conflict section that appears above matches
 */
export const ConflictSectionSkeleton: React.FC = () => {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5 }}>
        <Skeleton variant="text" width={150} height={24} animation="wave" sx={skeletonSx} />
        <Skeleton variant="text" width={16} height={16} animation="wave" sx={{ ml: 1, ...skeletonSx }} />
      </Box>
    </Box>
  );
};

/**
 * Skeleton for the entire schedule list
 */
export const ScheduleListSkeleton: React.FC = () => {
  return (
    <Box>
      {/* Show 3 days of skeleton matches */}
      <DayMatchesSkeleton matchCount={2} />
      <DayMatchesSkeleton matchCount={3} />
      <DayMatchesSkeleton matchCount={1} />
    </Box>
  );
};

/**
 * Skeleton for departure time display within a match card
 * Can be used inline when departure time is still calculating
 */
export const DepartureTimeSkeleton: React.FC = () => {
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Skeleton variant="circular" width={24} height={24} animation="wave" sx={skeletonSx} />
      <Box>
        <Skeleton variant="text" width={100} height={18} animation="wave" sx={skeletonSx} />
        <Skeleton variant="text" width={140} height={16} animation="wave" sx={skeletonSx} />
      </Box>
    </Box>
  );
};

/**
 * Skeleton that mimics a standings table (Pos, Team, Pl, W-D-L, Pts)
 */
export const StandingsTableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => {
  return (
    <Box>
      {Array.from({ length: rows }).map((_, i) => (
        <React.Fragment key={i}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1 }}>
            <Skeleton variant="text" width={20} height={18} animation="wave" sx={skeletonSx} />
            <Skeleton variant="rounded" width={24} height={24} animation="wave" sx={skeletonSx} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={18} animation="wave" sx={skeletonSx} />
              <Skeleton variant="text" width="40%" height={14} animation="wave" sx={skeletonSx} />
            </Box>
          </Box>
          {i < rows - 1 && <Divider />}
        </React.Fragment>
      ))}
    </Box>
  );
};

/**
 * Skeleton for a weekly calendar list view (mimics FullCalendar list layout)
 */
export const WeeklyCalendarSkeleton: React.FC = () => {
  return (
    <Box>
      {Array.from({ length: 5 }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          {/* Date column */}
          <Box sx={{ minWidth: 60 }}>
            <Skeleton variant="text" width={50} height={18} animation="wave" sx={skeletonSx} />
            <Skeleton variant="text" width={30} height={14} animation="wave" sx={skeletonSx} />
          </Box>
          {/* Event bar */}
          <Skeleton
            variant="rectangular"
            width="100%"
            height={28}
            animation="wave"
            sx={{ borderRadius: '4px', ...skeletonSx }}
          />
        </Box>
      ))}
    </Box>
  );
};

/**
 * Skeleton that mimics a month calendar grid (7 cols x 5 rows of day cells)
 */
export const CalendarGridSkeleton: React.FC = () => {
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box>
      {/* Weekday headers */}
      <Grid container spacing={0} sx={{ mb: 1 }}>
        {weekdays.map(day => (
          <Grid item xs key={day} sx={{ textAlign: 'center' }}>
            <Skeleton variant="text" width={28} height={24} animation="wave" sx={{ mx: 'auto', ...skeletonSx }} />
          </Grid>
        ))}
      </Grid>

      {/* 5 weeks of day cells */}
      {Array.from({ length: 5 }).map((_, weekIndex) => (
        <Grid container spacing={0} key={weekIndex}>
          {Array.from({ length: 7 }).map((_, dayIndex) => (
            <Grid item xs key={dayIndex}>
              <Box
                sx={{
                  minHeight: { xs: '100px', sm: '120px' },
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1,
                }}
              >
                <Skeleton variant="text" width={16} height={20} animation="wave" sx={skeletonSx} />
                {/* Random event bars */}
                {(weekIndex + dayIndex) % 3 === 0 && (
                  <Skeleton
                    variant="rectangular"
                    width="90%"
                    height={14}
                    animation="wave"
                    sx={{ borderRadius: '4px', mt: 0.5, ...skeletonSx }}
                  />
                )}
                {(weekIndex + dayIndex) % 4 === 0 && (
                  <Skeleton
                    variant="rectangular"
                    width="80%"
                    height={14}
                    animation="wave"
                    sx={{ borderRadius: '4px', mt: 0.5, ...skeletonSx }}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      ))}
    </Box>
  );
};
